/**
 * Canonical ReHub one-time credit packs + monthly credit subscriptions.
 *
 * Amounts are stored in integer minor units (pence / cents). Never use
 * floating-point arithmetic for payment amounts — convert for display only.
 *
 * Stable pack IDs (`credits_10`, …) are the primary identifiers for checkout
 * metadata and analytics. Legacy `topup_*` aliases remain resolvable so existing
 * Stripe metadata / older clients keep working.
 */

import {
  BILLING_CURRENCY_STORAGE_KEY,
  formatCheckoutTotal,
  formatMoney,
  formatMoneyFromMinor,
  formatPerUnitFromMinor,
  minorToMajor,
  parseRehubCurrency,
  REHUB_CURRENCIES,
  type RehubCurrency,
} from './currency';
import {
  PROPOSED_EUR_CREDIT_MONTHLY_V1,
  PROPOSED_EUR_CREDIT_PACKS_V1,
} from './proposed-eur';

export type CreditCurrency = RehubCurrency;

export type RehubCreditPackId =
  | 'credits_10'
  | 'credits_25'
  | 'credits_50'
  | 'credits_100'
  | 'credits_150'
  | 'credits_300';

export type RehubCreditSubscriptionId =
  | 'starter'
  | 'regular'
  | 'pro'
  | 'business'
  | 'agency'
  | 'enterprise';

/** Legacy plan ids still accepted by resolve helpers / older checkout metadata. */
export type LegacyCreditSubscriptionId = 'elite' | 'ultra';

export type RehubCreditProductId = RehubCreditPackId | RehubCreditSubscriptionId;

/** Legacy IDs still accepted by resolve helpers / older UI. */
export type LegacyCreditTopupId = 'topup_10' | 'topup_25' | 'topup_50' | 'topup_100' | 'topup_150' | 'topup_300';

export interface RehubCreditPackDefinition {
  /** Stable pack identifier used in checkout metadata and analytics. */
  id: RehubCreditPackId;
  /** Legacy catalogue id (topup_N). Kept for backwards compatibility. */
  legacyId: LegacyCreditTopupId;
  credits: number;
  /** Display label, e.g. "25 Credits". */
  label: string;
  /** Short CTA, e.g. "Buy 25 credits". */
  ctaLabel: string;
  /** Integer minor units: GBP pence, USD cents. */
  amountMinor: Record<CreditCurrency, number>;
  /** Pre-formatted per-credit display strings (fixed localised rates). */
  perCredit: Record<CreditCurrency, string>;
  /** Sort order ascending: 10 → 25 → 50 → 100 → 150 → 300. */
  sortOrder: number;
  /** Optional badge shown on the pack card. */
  badge?: 'popular' | 'high-volume';
  /** Hardcoded Stripe Price IDs when known; env overrides take precedence. */
  defaultStripePriceIds: Partial<Record<CreditCurrency, string>>;
}

export interface RehubCreditSubscriptionDefinition {
  id: RehubCreditSubscriptionId;
  credits: number;
  label: string;
  amountMinor: Record<CreditCurrency, number>;
  perCredit: Record<CreditCurrency, string>;
  sortOrder: number;
  badge?: 'popular';
  defaultStripePriceIds: Partial<Record<CreditCurrency, string>>;
}

export interface RehubCustomCreditPricing {
  minCredits: number;
  maxCredits: number;
  /** Minor units per credit (integer). */
  amountMinorPerCredit: Record<CreditCurrency, number>;
  defaultStripePriceIds: Partial<Record<CreditCurrency, string>>;
}

/** Session key for persisting the user's currency choice while the panel is reopened. */
export const CREDIT_CURRENCY_SESSION_KEY = 'rehub_credit_currency';

export const REHUB_LOGO_URL = 'https://hub.rehub.studio/ReHub-Logo-White.png';

/**
 * One-time top-up packs — single source of truth for UI, checkout, and docs.
 * Do not duplicate these amounts in app modals or webhook handlers.
 */
export const REHUB_CREDIT_PACKS: readonly RehubCreditPackDefinition[] = [
  {
    id: 'credits_10',
    legacyId: 'topup_10',
    credits: 10,
    label: '10 Credits',
    ctaLabel: 'Buy 10 credits',
    amountMinor: { GBP: 850, EUR: PROPOSED_EUR_CREDIT_PACKS_V1.credits_10.amountMinor, USD: 1150 },
    perCredit: { GBP: '£0.85', EUR: PROPOSED_EUR_CREDIT_PACKS_V1.credits_10.perCredit, USD: '$1.15' },
    sortOrder: 10,
    defaultStripePriceIds: {
      GBP: 'price_1SpTVjDHlg75FZHHDhSjNgUv',
      EUR: 'price_1UESlMDHlg75FZHHypnWypQm',
      USD: 'price_1SpTSkDHlg75FZHHzDlT5Wl6',
    },
  },
  {
    id: 'credits_25',
    legacyId: 'topup_25',
    credits: 25,
    label: '25 Credits',
    ctaLabel: 'Buy 25 credits',
    amountMinor: { GBP: 2000, EUR: PROPOSED_EUR_CREDIT_PACKS_V1.credits_25.amountMinor, USD: 2800 },
    perCredit: { GBP: '£0.80', EUR: PROPOSED_EUR_CREDIT_PACKS_V1.credits_25.perCredit, USD: '$1.12' },
    sortOrder: 20,
    defaultStripePriceIds: {
      EUR: 'price_1UESluDHlg75FZHHLUrxVqQA',
    },
  },
  {
    id: 'credits_50',
    legacyId: 'topup_50',
    credits: 50,
    label: '50 Credits',
    ctaLabel: 'Buy 50 credits',
    amountMinor: { GBP: 4000, EUR: PROPOSED_EUR_CREDIT_PACKS_V1.credits_50.amountMinor, USD: 5500 },
    perCredit: { GBP: '£0.80', EUR: PROPOSED_EUR_CREDIT_PACKS_V1.credits_50.perCredit, USD: '$1.10' },
    sortOrder: 30,
    badge: 'popular',
    defaultStripePriceIds: {
      GBP: 'price_1SpTWiDHlg75FZHHnMyf7HD2',
      EUR: 'price_1UESlNDHlg75FZHHQjyXtM8n',
      USD: 'price_1SpTTPDHlg75FZHHCzXrblKs',
    },
  },
  {
    id: 'credits_100',
    legacyId: 'topup_100',
    credits: 100,
    label: '100 Credits',
    ctaLabel: 'Buy 100 credits',
    amountMinor: { GBP: 7500, EUR: PROPOSED_EUR_CREDIT_PACKS_V1.credits_100.amountMinor, USD: 10000 },
    perCredit: { GBP: '£0.75', EUR: PROPOSED_EUR_CREDIT_PACKS_V1.credits_100.perCredit, USD: '$1.00' },
    sortOrder: 40,
    defaultStripePriceIds: {
      EUR: 'price_1UESlvDHlg75FZHHQPi79Eov',
    },
  },
  {
    id: 'credits_150',
    legacyId: 'topup_150',
    credits: 150,
    label: '150 Credits',
    ctaLabel: 'Buy 150 credits',
    amountMinor: { GBP: 11250, EUR: PROPOSED_EUR_CREDIT_PACKS_V1.credits_150.amountMinor, USD: 15000 },
    perCredit: { GBP: '£0.75', EUR: PROPOSED_EUR_CREDIT_PACKS_V1.credits_150.perCredit, USD: '$1.00' },
    sortOrder: 50,
    badge: 'high-volume',
    defaultStripePriceIds: {
      GBP: 'price_1SpTbGDHlg75FZHHXkDeuWxH',
      EUR: 'price_1UESlPDHlg75FZHHV4zpE0Gi',
      USD: 'price_1SpTUjDHlg75FZHHoeErP4Pc',
    },
  },
  {
    id: 'credits_300',
    legacyId: 'topup_300',
    credits: 300,
    label: '300 Credits',
    ctaLabel: 'Buy 300 credits',
    amountMinor: { GBP: 22500, EUR: PROPOSED_EUR_CREDIT_PACKS_V1.credits_300.amountMinor, USD: 30000 },
    perCredit: { GBP: '£0.75', EUR: PROPOSED_EUR_CREDIT_PACKS_V1.credits_300.perCredit, USD: '$1.00' },
    sortOrder: 60,
    badge: 'high-volume',
    defaultStripePriceIds: {
      GBP: 'price_1SpsnvDHlg75FZHHxbDny3MR',
      EUR: 'price_1UESlRDHlg75FZHH2SRBYTQR',
      USD: 'price_1Sptl5DHlg75FZHHIx4BYu8V',
    },
  },
] as const;

export const REHUB_CREDIT_MONTHLY_PLANS: readonly RehubCreditSubscriptionDefinition[] = [
  {
    id: 'starter',
    credits: 30,
    label: 'Starter',
    amountMinor: { GBP: 2250, EUR: PROPOSED_EUR_CREDIT_MONTHLY_V1.starter.amountMinor, USD: 3000 },
    perCredit: { GBP: '£0.75', EUR: PROPOSED_EUR_CREDIT_MONTHLY_V1.starter.perCredit, USD: '$1.00' },
    sortOrder: 10,
    // Existing Stripe prices — keep for active subscribers / backwards compatibility.
    defaultStripePriceIds: {
      GBP: 'price_1SpTMpDHlg75FZHHob8v9Wua',
      EUR: 'price_1UESlYDHlg75FZHH9EB0QVhj',
      USD: 'price_1SpT7NDHlg75FZHH58gPxp3m',
    },
  },
  {
    id: 'regular',
    credits: 75,
    label: 'Regular',
    amountMinor: { GBP: 4950, EUR: PROPOSED_EUR_CREDIT_MONTHLY_V1.regular.amountMinor, USD: 6500 },
    perCredit: { GBP: '£0.66', EUR: PROPOSED_EUR_CREDIT_MONTHLY_V1.regular.perCredit, USD: '$0.87' },
    sortOrder: 20,
    // EUR is live; GBP/USD still come from env until those Price IDs are stored here.
    defaultStripePriceIds: {
      EUR: 'price_1UESm5DHlg75FZHH4KenYV7g',
    },
  },
  {
    id: 'pro',
    credits: 125,
    label: 'Pro',
    amountMinor: { GBP: 7500, EUR: PROPOSED_EUR_CREDIT_MONTHLY_V1.pro.amountMinor, USD: 10000 },
    perCredit: { GBP: '£0.60', EUR: PROPOSED_EUR_CREDIT_MONTHLY_V1.pro.perCredit, USD: '$0.80' },
    sortOrder: 30,
    badge: 'popular',
    defaultStripePriceIds: {
      GBP: 'price_1SpTO5DHlg75FZHH5IUsHDTj',
      EUR: 'price_1UESlZDHlg75FZHHsKjtAOYI',
      USD: 'price_1SpTAqDHlg75FZHHub6RaHL5',
    },
  },
  {
    id: 'business',
    credits: 350,
    label: 'Business',
    amountMinor: { GBP: 18750, EUR: PROPOSED_EUR_CREDIT_MONTHLY_V1.business.amountMinor, USD: 25000 },
    perCredit: { GBP: '£0.54', EUR: PROPOSED_EUR_CREDIT_MONTHLY_V1.business.perCredit, USD: '$0.71' },
    sortOrder: 40,
    // Same Stripe prices as legacy "Elite".
    defaultStripePriceIds: {
      GBP: 'price_1SpTPZDHlg75FZHHl835dmDA',
      EUR: 'price_1UESlbDHlg75FZHHtkBCjmsk',
      USD: 'price_1SpTBpDHlg75FZHH6FLUSZ1U',
    },
  },
  {
    id: 'agency',
    credits: 750,
    label: 'Agency',
    amountMinor: { GBP: 39000, EUR: PROPOSED_EUR_CREDIT_MONTHLY_V1.agency.amountMinor, USD: 52500 },
    perCredit: { GBP: '£0.52', EUR: PROPOSED_EUR_CREDIT_MONTHLY_V1.agency.perCredit, USD: '$0.70' },
    sortOrder: 50,
    defaultStripePriceIds: {
      EUR: 'price_1UESleDHlg75FZHHFFyBkROb',
    },
  },
  {
    id: 'enterprise',
    credits: 1500,
    label: 'Enterprise',
    amountMinor: { GBP: 75000, EUR: PROPOSED_EUR_CREDIT_MONTHLY_V1.enterprise.amountMinor, USD: 100000 },
    perCredit: { GBP: '£0.50', EUR: PROPOSED_EUR_CREDIT_MONTHLY_V1.enterprise.perCredit, USD: '$0.67' },
    sortOrder: 60,
    // Same Stripe prices as legacy "Ultra".
    defaultStripePriceIds: {
      GBP: 'price_1SpTQgDHlg75FZHH9GTFTgDF',
      EUR: 'price_1UESldDHlg75FZHHaxPCZPzB',
      USD: 'price_1SpTJYDHlg75FZHHzG1HxRbn',
    },
  },
] as const;

/** Custom / exact-credit purchases — full per-credit rate, no pack discount. */
export const REHUB_CUSTOM_CREDIT_PRICING: RehubCustomCreditPricing = {
  minCredits: 5,
  maxCredits: 500,
  amountMinorPerCredit: { GBP: 75, EUR: 95, USD: 105 },
  defaultStripePriceIds: {
    GBP: 'price_1T1NXsDHlg75FZHHmn69RRi7',
    EUR: 'price_1UESlRDHlg75FZHHBWcFNkKH',
    USD: 'price_1T1NYIDHlg75FZHH46JYdTMI',
  },
};

type StripePriceEnv = Partial<Record<string, string | undefined>>;

function envKeysFor(base: string): Record<CreditCurrency, string> {
  return {
    GBP: `${base}_GBP`,
    EUR: `${base}_EUR`,
    USD: `${base}_USD`,
  };
}

const PACK_ENV_KEYS: Record<RehubCreditPackId, Record<CreditCurrency, string>> = {
  credits_10: envKeysFor('VITE_STRIPE_PRICE_CREDITS_10'),
  credits_25: envKeysFor('VITE_STRIPE_PRICE_CREDITS_25'),
  credits_50: envKeysFor('VITE_STRIPE_PRICE_CREDITS_50'),
  credits_100: envKeysFor('VITE_STRIPE_PRICE_CREDITS_100'),
  credits_150: envKeysFor('VITE_STRIPE_PRICE_CREDITS_150'),
  credits_300: envKeysFor('VITE_STRIPE_PRICE_CREDITS_300'),
};

const SUB_ENV_KEYS: Record<RehubCreditSubscriptionId, Record<CreditCurrency, string>> = {
  starter: envKeysFor('VITE_STRIPE_PRICE_SUB_STARTER'),
  regular: envKeysFor('VITE_STRIPE_PRICE_SUB_REGULAR'),
  pro: envKeysFor('VITE_STRIPE_PRICE_SUB_PRO'),
  business: envKeysFor('VITE_STRIPE_PRICE_SUB_BUSINESS'),
  agency: envKeysFor('VITE_STRIPE_PRICE_SUB_AGENCY'),
  enterprise: envKeysFor('VITE_STRIPE_PRICE_SUB_ENTERPRISE'),
};

/** Alternate env keys (credit-quantity based) for the new Regular / Agency plans. */
const SUB_MONTHLY_QUANTITY_ENV: Partial<
  Record<RehubCreditSubscriptionId, Record<CreditCurrency, string[]>>
> = {
  regular: {
    GBP: ['VITE_STRIPE_PRICE_MONTHLY_75_GBP', 'STRIPE_PRICE_MONTHLY_75_GBP'],
    EUR: ['VITE_STRIPE_PRICE_MONTHLY_75_EUR', 'STRIPE_PRICE_MONTHLY_75_EUR'],
    USD: ['VITE_STRIPE_PRICE_MONTHLY_75_USD', 'STRIPE_PRICE_MONTHLY_75_USD'],
  },
  agency: {
    GBP: ['VITE_STRIPE_PRICE_MONTHLY_750_GBP', 'STRIPE_PRICE_MONTHLY_750_GBP'],
    EUR: ['VITE_STRIPE_PRICE_MONTHLY_750_EUR', 'STRIPE_PRICE_MONTHLY_750_EUR'],
    USD: ['VITE_STRIPE_PRICE_MONTHLY_750_USD', 'STRIPE_PRICE_MONTHLY_750_USD'],
  },
};

/** Maps legacy Elite/Ultra ids onto Business/Enterprise. */
const LEGACY_TO_PLAN: Record<string, RehubCreditSubscriptionId> = {
  starter: 'starter',
  regular: 'regular',
  pro: 'pro',
  business: 'business',
  agency: 'agency',
  enterprise: 'enterprise',
  elite: 'business',
  ultra: 'enterprise',
};

const LEGACY_TO_PACK: Record<string, RehubCreditPackId> = {
  topup_10: 'credits_10',
  topup_25: 'credits_25',
  topup_50: 'credits_50',
  topup_100: 'credits_100',
  topup_150: 'credits_150',
  topup_300: 'credits_300',
  credits_10: 'credits_10',
  credits_25: 'credits_25',
  credits_50: 'credits_50',
  credits_100: 'credits_100',
  credits_150: 'credits_150',
  credits_300: 'credits_300',
};

export function normalizeCreditPackId(id: string | null | undefined): RehubCreditPackId | null {
  if (!id) return null;
  return LEGACY_TO_PACK[id] ?? null;
}

export function normalizeCreditSubscriptionId(
  id: string | null | undefined
): RehubCreditSubscriptionId | null {
  if (!id) return null;
  return LEGACY_TO_PLAN[id] ?? null;
}

export function getCreditPack(id: string | null | undefined): RehubCreditPackDefinition | null {
  const normalized = normalizeCreditPackId(id);
  if (!normalized) return null;
  return REHUB_CREDIT_PACKS.find((p) => p.id === normalized) ?? null;
}

export function getCreditSubscription(id: string | null | undefined): RehubCreditSubscriptionDefinition | null {
  const normalized = normalizeCreditSubscriptionId(id);
  if (!normalized) return null;
  return REHUB_CREDIT_MONTHLY_PLANS.find((p) => p.id === normalized) ?? null;
}

export function formatMinorAmount(amountMinor: number, currency: CreditCurrency): string {
  return formatMoneyFromMinor(amountMinor, currency);
}

export function formatPackPriceFromMinor(amountMinor: number, currency: CreditCurrency): string {
  return formatCheckoutTotal(amountMinor, currency);
}

/** Format price-per-credit from minor units ÷ credits (display only). */
export function formatPerCreditFromMinor(
  amountMinor: number,
  credits: number,
  currency: CreditCurrency
): string {
  return formatPerUnitFromMinor(amountMinor, credits, currency);
}

export function getPackStripePriceId(
  pack: RehubCreditPackDefinition,
  currency: CreditCurrency,
  env: StripePriceEnv = {}
): string {
  const envKey = PACK_ENV_KEYS[pack.id][currency];
  const serverKey = envKey.replace(/^VITE_/, '');
  return (
    env[envKey] ||
    env[serverKey] ||
    env[`STRIPE_PRICE_${pack.id.toUpperCase()}_${currency}`] ||
    pack.defaultStripePriceIds[currency] ||
    ''
  );
}

export function getSubscriptionStripePriceId(
  plan: RehubCreditSubscriptionDefinition,
  currency: CreditCurrency,
  env: StripePriceEnv = {}
): string {
  const envKey = SUB_ENV_KEYS[plan.id][currency];
  const serverKey = envKey.replace(/^VITE_/, '');
  const quantityKeys = SUB_MONTHLY_QUANTITY_ENV[plan.id]?.[currency] || [];
  for (const key of quantityKeys) {
    if (env[key]) return env[key] as string;
  }
  // Legacy Elite / Ultra env aliases still map onto Business / Enterprise.
  const legacyAlias =
    plan.id === 'business'
      ? env[`VITE_STRIPE_PRICE_SUB_ELITE_${currency}`] || env[`STRIPE_PRICE_SUB_ELITE_${currency}`]
      : plan.id === 'enterprise'
        ? env[`VITE_STRIPE_PRICE_SUB_ULTRA_${currency}`] || env[`STRIPE_PRICE_SUB_ULTRA_${currency}`]
        : undefined;
  return (
    env[envKey] ||
    env[serverKey] ||
    legacyAlias ||
    plan.defaultStripePriceIds[currency] ||
    ''
  );
}

/**
 * Known Stripe Price ID → subscription plan (existing hardcoded IDs only).
 * New Regular / Agency prices are resolved from env at runtime via
 * `buildSubscriptionPriceCreditMap`.
 */
export const KNOWN_SUBSCRIPTION_PRICE_TO_PLAN: Record<
  string,
  { planId: RehubCreditSubscriptionId; credits: number; label: string }
> = {
  price_1SpT7NDHlg75FZHH58gPxp3m: { planId: 'starter', credits: 30, label: 'Starter' },
  price_1SpTMpDHlg75FZHHob8v9Wua: { planId: 'starter', credits: 30, label: 'Starter' },
  price_1UESlYDHlg75FZHH9EB0QVhj: { planId: 'starter', credits: 30, label: 'Starter' },
  price_1SpTAqDHlg75FZHHub6RaHL5: { planId: 'pro', credits: 125, label: 'Pro' },
  price_1SpTO5DHlg75FZHH5IUsHDTj: { planId: 'pro', credits: 125, label: 'Pro' },
  price_1UESlZDHlg75FZHHsKjtAOYI: { planId: 'pro', credits: 125, label: 'Pro' },
  price_1SpTBpDHlg75FZHH6FLUSZ1U: { planId: 'business', credits: 350, label: 'Business' },
  price_1SpTPZDHlg75FZHHl835dmDA: { planId: 'business', credits: 350, label: 'Business' },
  price_1UESlbDHlg75FZHHtkBCjmsk: { planId: 'business', credits: 350, label: 'Business' },
  price_1SpTJYDHlg75FZHHzG1HxRbn: { planId: 'enterprise', credits: 1500, label: 'Enterprise' },
  price_1SpTQgDHlg75FZHH9GTFTgDF: { planId: 'enterprise', credits: 1500, label: 'Enterprise' },
  price_1UESldDHlg75FZHHaxPCZPzB: { planId: 'enterprise', credits: 1500, label: 'Enterprise' },
  price_1UESm5DHlg75FZHH4KenYV7g: { planId: 'regular', credits: 75, label: 'Regular' },
  price_1UESleDHlg75FZHHFFyBkROb: { planId: 'agency', credits: 750, label: 'Agency' },
};

/** Build price-id → credits map including env-configured Regular / Agency prices. */
export function buildSubscriptionPriceCreditMap(
  env: StripePriceEnv = {}
): Record<string, { planId: RehubCreditSubscriptionId; credits: number; label: string }> {
  const map: Record<string, { planId: RehubCreditSubscriptionId; credits: number; label: string }> = {
    ...KNOWN_SUBSCRIPTION_PRICE_TO_PLAN,
  };
  for (const plan of REHUB_CREDIT_MONTHLY_PLANS) {
    for (const currency of REHUB_CURRENCIES) {
      const priceId = getSubscriptionStripePriceId(plan, currency, env);
      if (priceId) {
        map[priceId] = { planId: plan.id, credits: plan.credits, label: plan.label };
      }
    }
  }
  return map;
}

export function resolveSubscriptionByPriceId(
  priceId: string | null | undefined,
  env: StripePriceEnv = {}
): { planId: RehubCreditSubscriptionId; credits: number; label: string } | null {
  if (!priceId) return null;
  return buildSubscriptionPriceCreditMap(env)[priceId] ?? null;
}

export function getCustomCreditStripePriceId(
  currency: CreditCurrency,
  env: StripePriceEnv = {}
): string {
  const viteKey = `VITE_STRIPE_PRICE_PER_CREDIT_${currency}`;
  const serverKey = `STRIPE_PRICE_PER_CREDIT_${currency}`;
  return (
    env[viteKey] ||
    env[serverKey] ||
    REHUB_CUSTOM_CREDIT_PRICING.defaultStripePriceIds[currency] ||
    ''
  );
}

/**
 * Resolve a one-time pack for secure checkout. Prefer Stripe Price IDs when
 * configured; otherwise callers may use `price_data` with `amountMinor`.
 */
export function resolveCreditPackCheckout(
  packId: string,
  currency: CreditCurrency,
  env: StripePriceEnv = {}
): {
  pack: RehubCreditPackDefinition;
  priceId: string;
  amountMinor: number;
  credits: number;
  purchaseType: 'credit_pack';
} | null {
  const pack = getCreditPack(packId);
  if (!pack) return null;
  return {
    pack,
    priceId: getPackStripePriceId(pack, currency, env),
    amountMinor: pack.amountMinor[currency],
    credits: pack.credits,
    purchaseType: 'credit_pack',
  };
}

export function resolveSubscriptionCheckout(
  planId: string,
  currency: CreditCurrency,
  env: StripePriceEnv = {}
): {
  plan: RehubCreditSubscriptionDefinition;
  priceId: string;
  amountMinor: number;
  credits: number;
  purchaseType: 'credit_subscription';
} | null {
  const plan = getCreditSubscription(planId);
  if (!plan) return null;
  const priceId = getSubscriptionStripePriceId(plan, currency, env);
  if (!priceId) return null;
  return {
    plan,
    priceId,
    amountMinor: plan.amountMinor[currency],
    credits: plan.credits,
    purchaseType: 'credit_subscription',
  };
}

export function clampCustomCredits(amount: number): number {
  const { minCredits, maxCredits } = REHUB_CUSTOM_CREDIT_PRICING;
  if (!Number.isFinite(amount)) return minCredits;
  return Math.min(maxCredits, Math.max(minCredits, Math.floor(amount)));
}

export function customCreditsTotalMinor(credits: number, currency: CreditCurrency): number {
  const qty = clampCustomCredits(credits);
  return qty * REHUB_CUSTOM_CREDIT_PRICING.amountMinorPerCredit[currency];
}

/** Persist currency across visits (manual choice wins until changed). */
export function readStoredCreditCurrency(fallback: CreditCurrency = 'GBP'): CreditCurrency {
  try {
    const raw =
      localStorage.getItem(BILLING_CURRENCY_STORAGE_KEY)
      || sessionStorage.getItem(BILLING_CURRENCY_STORAGE_KEY)
      || sessionStorage.getItem(CREDIT_CURRENCY_SESSION_KEY);
    return parseRehubCurrency(raw) ?? fallback;
  } catch {
    /* ignore */
  }
  return fallback;
}

export function storeCreditCurrency(currency: CreditCurrency): void {
  try {
    localStorage.setItem(BILLING_CURRENCY_STORAGE_KEY, currency);
    sessionStorage.setItem(CREDIT_CURRENCY_SESSION_KEY, currency);
    sessionStorage.setItem(BILLING_CURRENCY_STORAGE_KEY, currency);
  } catch {
    /* ignore */
  }
}

/** Legacy display shape used by marketing sites and older call sites. */
export interface RehubCreditPack {
  id: string;
  name: string;
  credits: number;
  popular?: boolean;
  price: Record<CreditCurrency, number>;
  perCredit: Record<CreditCurrency, string>;
}

/**
 * Display-oriented catalogue (major-unit prices) for marketing sites that still
 * consume the older `RehubCreditPack` shape via `REHUB_CREDIT_TOPUPS`.
 */
export function toDisplayTopups(): RehubCreditPack[] {
  return REHUB_CREDIT_PACKS.map((p) => ({
    id: p.legacyId,
    name: p.label,
    credits: p.credits,
    popular: p.badge === 'popular',
    price: {
      GBP: minorToMajor(p.amountMinor.GBP),
      EUR: minorToMajor(p.amountMinor.EUR),
      USD: minorToMajor(p.amountMinor.USD),
    },
    perCredit: { ...p.perCredit },
  }));
}

export function toDisplaySubscriptions(): RehubCreditPack[] {
  return REHUB_CREDIT_MONTHLY_PLANS.map((p) => ({
    id: p.id,
    name: p.label,
    credits: p.credits,
    popular: p.badge === 'popular',
    price: {
      GBP: minorToMajor(p.amountMinor.GBP),
      EUR: minorToMajor(p.amountMinor.EUR),
      USD: minorToMajor(p.amountMinor.USD),
    },
    perCredit: { ...p.perCredit },
  }));
}

/** One-time packs in the older major-unit display shape. */
export const REHUB_CREDIT_TOPUPS: RehubCreditPack[] = toDisplayTopups();

/** Monthly plans in the older major-unit display shape. */
export const REHUB_CREDIT_SUBSCRIPTIONS: RehubCreditPack[] = toDisplaySubscriptions();

export function formatPackPrice(amount: number, currency: CreditCurrency): string {
  return formatMoney(amount, currency);
}

/** Env keys that apps / docs should document for new 25 & 100 packs. */
export const CREDIT_PACK_STRIPE_ENV_KEYS = {
  credits_25: {
    GBP: 'VITE_STRIPE_PRICE_CREDITS_25_GBP',
    EUR: 'VITE_STRIPE_PRICE_CREDITS_25_EUR',
    USD: 'VITE_STRIPE_PRICE_CREDITS_25_USD',
  },
  credits_100: {
    GBP: 'VITE_STRIPE_PRICE_CREDITS_100_GBP',
    EUR: 'VITE_STRIPE_PRICE_CREDITS_100_EUR',
    USD: 'VITE_STRIPE_PRICE_CREDITS_100_USD',
  },
  perCredit: {
    GBP: 'VITE_STRIPE_PRICE_PER_CREDIT_GBP',
    EUR: 'VITE_STRIPE_PRICE_PER_CREDIT_EUR',
    USD: 'VITE_STRIPE_PRICE_PER_CREDIT_USD',
  },
} as const;

/** Env keys for the new Regular (75) and Agency (750) monthly plans. */
export const CREDIT_MONTHLY_STRIPE_ENV_KEYS = {
  regular_75: {
    GBP: 'VITE_STRIPE_PRICE_MONTHLY_75_GBP',
    EUR: 'VITE_STRIPE_PRICE_MONTHLY_75_EUR',
    USD: 'VITE_STRIPE_PRICE_MONTHLY_75_USD',
    altGBP: 'STRIPE_PRICE_MONTHLY_75_GBP',
    altEUR: 'STRIPE_PRICE_MONTHLY_75_EUR',
    altUSD: 'STRIPE_PRICE_MONTHLY_75_USD',
  },
  agency_750: {
    GBP: 'VITE_STRIPE_PRICE_MONTHLY_750_GBP',
    EUR: 'VITE_STRIPE_PRICE_MONTHLY_750_EUR',
    USD: 'VITE_STRIPE_PRICE_MONTHLY_750_USD',
    altGBP: 'STRIPE_PRICE_MONTHLY_750_GBP',
    altEUR: 'STRIPE_PRICE_MONTHLY_750_EUR',
    altUSD: 'STRIPE_PRICE_MONTHLY_750_USD',
  },
} as const;

/**
 * Legacy (v1) dashboard / embed catalogue. New-customer surfaces should use
 * `buildDashboardProductCatalog` from credit-packs-v2.ts.
 */
export function buildLegacyDashboardProductCatalog(env: StripePriceEnv = {}) {
  return {
    SUBSCRIPTIONS: REHUB_CREDIT_MONTHLY_PLANS.map((p) => ({
      id: p.id,
      name: p.label,
      credits: p.credits,
      bonus: 0,
      popular: p.badge === 'popular',
      costPerCredit: {
        USD: `${p.perCredit.USD} per credit`,
        EUR: `${p.perCredit.EUR} per credit`,
        GBP: `${p.perCredit.GBP} per credit`,
      },
      valueLabel: null as string | null,
      thirdFeature: null as string | null,
      prices: {
        USD: {
          id: getSubscriptionStripePriceId(p, 'USD', env),
          amount: minorToMajor(p.amountMinor.USD),
        },
        EUR: {
          id: getSubscriptionStripePriceId(p, 'EUR', env),
          amount: minorToMajor(p.amountMinor.EUR),
        },
        GBP: {
          id: getSubscriptionStripePriceId(p, 'GBP', env),
          amount: minorToMajor(p.amountMinor.GBP),
        },
      },
    })),
    TOPUPS: REHUB_CREDIT_PACKS.map((p) => ({
      id: p.legacyId,
      name: `Top Up ${p.credits}`,
      credits: p.credits,
      bonus: 0,
      popular: p.badge === 'popular',
      costPerCredit: {
        USD: `${p.perCredit.USD} per credit`,
        EUR: `${p.perCredit.EUR} per credit`,
        GBP: `${p.perCredit.GBP} per credit`,
      },
      prices: {
        USD: {
          id: getPackStripePriceId(p, 'USD', env),
          amount: minorToMajor(p.amountMinor.USD),
        },
        EUR: {
          id: getPackStripePriceId(p, 'EUR', env),
          amount: minorToMajor(p.amountMinor.EUR),
        },
        GBP: {
          id: getPackStripePriceId(p, 'GBP', env),
          amount: minorToMajor(p.amountMinor.GBP),
        },
      },
    })),
  };
}
